import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Scroll-reveal wrapper. Fades + lifts its children into view the first time
 * they enter the viewport. Respects prefers-reduced-motion (the CSS .reveal
 * rule no-ops under reduced motion, and we also reveal immediately if the
 * IntersectionObserver API is unavailable).
 */
export function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting || e.boundingClientRect.top < 0) {
            setShown(true);
            obs.disconnect();
          }
        }
      },
      // Root box extends 100000px ABOVE the viewport: anything the user has
      // scrolled past (anchor links, fast flicks) counts as intersecting and
      // reveals — jumped-past sections must never stay invisible when the
      // user scrolls back up. The -8% bottom margin still gates the fade-in
      // until an element is genuinely entering from below.
      { threshold: 0.15, rootMargin: "100000px 0px -8% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${shown ? "is-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}
